import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, CheckCircle, ShoppingCart, XCircle, Eye, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { quotesAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { usePagination, Pagination } from '../components/Pagination';

const QUOTE_STATUS = {
    pending: { label: 'En attente', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
    responded: { label: 'Repondu', icon: CheckCircle, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    converted: { label: 'Converti', icon: ShoppingCart, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    cancelled: { label: 'Annule', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200' },
};

const StatusBadge = ({ status }) => {
    const cfg = QUOTE_STATUS[status] || QUOTE_STATUS.pending;
    const Icon = cfg.icon;
    return (
        <Badge variant="outline" className={`rounded-full text-xs font-medium ${cfg.color}`}>
            <Icon className="w-3 h-3 mr-1" />
            {cfg.label}
        </Badge>
    );
};

export const ClientQuotes = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        const loadQuotes = async () => {
            try {
                const res = await quotesAPI.getMyQuotes();
                setQuotes(res.data.quotes || []);
            } catch (error) {
                console.error('Error loading quotes:', error);
            } finally {
                setLoading(false);
            }
        };
        loadQuotes();
    }, []);

    const filteredQuotes = statusFilter === 'all'
        ? quotes
        : quotes.filter(q => q.status === statusFilter);

    const { paginatedItems, currentPage, totalPages, setCurrentPage, totalItems } = usePagination(filteredQuotes, 50);

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="client-quotes-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Mes Demandes de Prix</h1>
                            <p className="text-muted-foreground">{quotes.length} demande{quotes.length !== 1 ? 's' : ''} de prix</p>
                        </div>
                    </div>
                    <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl">
                        <Link to="/catalogue">
                            <FileText className="h-4 w-4 mr-2" />
                            Nouvelle demande
                        </Link>
                    </Button>
                </div>

                {/* Filter */}
                <Card className="mb-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardContent className="pt-6">
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-[200px] bg-white/80 border-jungle/20 rounded-xl" data-testid="quote-status-filter">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(QUOTE_STATUS).map(([key, { label }]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                {/* Quotes Table */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading text-jungle">
                            {filteredQuotes.length} demande{filteredQuotes.length !== 1 ? 's' : ''} de prix
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {filteredQuotes.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-jungle/10">
                                            <TableHead className="text-jungle/70">N° Demande</TableHead>
                                            <TableHead className="text-jungle/70">Date</TableHead>
                                            <TableHead className="text-jungle/70 text-center">Produits</TableHead>
                                            <TableHead className="text-jungle/70">Statut</TableHead>
                                            <TableHead className="text-jungle/70 text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedItems.map((q) => (
                                            <TableRow key={q.id} className="border-jungle/10" data-testid={`quote-row-${q.id}`}>
                                                <TableCell className="font-mono text-sm font-medium text-jungle">{q.quote_number}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground">{formatDateTime(q.created_at)}</TableCell>
                                                <TableCell className="text-center">
                                                    <Badge variant="outline" className="rounded-full border-jungle/20">{q.items?.length || 0}</Badge>
                                                </TableCell>
                                                <TableCell><StatusBadge status={q.status} /></TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="sm" className="hover:bg-leaf/10"
                                                        onClick={() => { setSelectedQuote(q); setDetailOpen(true); }}
                                                        data-testid={`view-quote-${q.id}`}>
                                                        <Eye className="h-4 w-4 text-leaf mr-1" />
                                                        Details
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                                <p className="text-muted-foreground mb-4">Aucune demande de prix</p>
                                <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl">
                                    <Link to="/catalogue">Parcourir le catalogue</Link>
                                </Button>
                            </div>
                        )}
                    </CardContent>
                    <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} setCurrentPage={setCurrentPage} label="demandes de prix" />
                </Card>
            </div>

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-sand" data-testid="client-quote-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                            <FileText className="w-5 h-5 text-amber-700" />
                            Demande {selectedQuote?.quote_number}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedQuote && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <StatusBadge status={selectedQuote.status} />
                                <span className="text-sm text-muted-foreground">{formatDateTime(selectedQuote.created_at)}</span>
                            </div>
                            <div className="p-4 rounded-xl bg-white/60 border border-jungle/10">
                                <h3 className="text-sm font-semibold text-jungle mb-3 flex items-center gap-2">
                                    <Package className="w-4 h-4" /> Produits demandes ({selectedQuote.items?.length || 0})
                                </h3>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-jungle/70">Produit</TableHead>
                                            <TableHead className="text-jungle/70 text-center">Quantite</TableHead>
                                            <TableHead className="text-jungle/70 text-center">Unite</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedQuote.items?.map((item, idx) => (
                                            <TableRow key={idx} className="border-jungle/10">
                                                <TableCell className="font-medium text-jungle">{item.product_name}</TableCell>
                                                <TableCell className="text-center">{item.quantity}</TableCell>
                                                <TableCell className="text-center text-muted-foreground">{item.unit}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            {selectedQuote.response_note && (
                                <div className="p-4 rounded-xl bg-blue-50/60 border border-blue-200">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-1">Reponse de PRIXDEAL</h3>
                                    <p className="text-sm text-blue-900">{selectedQuote.response_note}</p>
                                    {selectedQuote.responded_at && (
                                        <p className="text-xs text-blue-600 mt-2">Le {formatDateTime(selectedQuote.responded_at)}</p>
                                    )}
                                </div>
                            )}
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-xl">Fermer</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};
